import { useEffect, useRef } from "react";

export interface PageMetaOptions {
  title: string;
  description?: string;
  canonical?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function ensureMeta(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function ensureCanonical(url: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

function injectJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
  document.head
    .querySelectorAll('script[data-readdy-jsonld]')
    .forEach((n) => n.remove());
  const list = Array.isArray(data) ? data : [data];
  list.forEach((item) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-readdy-jsonld", "1");
    s.textContent = JSON.stringify(item);
    document.head.appendChild(s);
  });
}

export function usePageMeta(opts: PageMetaOptions) {
  const lastKey = useRef<string>("");

  useEffect(() => {
    const key = `${opts.title}|${opts.description || ""}|${JSON.stringify(opts.jsonLd || null)}`;
    if (key === lastKey.current) return;
    lastKey.current = key;

    if (opts.title) document.title = opts.title;
    if (opts.description) ensureMeta("description", opts.description);
    if (opts.canonical) ensureCanonical(opts.canonical);
    if (opts.jsonLd) injectJsonLd(opts.jsonLd);
  }, [opts.title, opts.description, opts.canonical, opts.jsonLd]);
}